import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';

const NAV_ITEMS = [
  {
    label: 'Dashboard',
    path: '/volunteer-dashboard',
    icon: 'LayoutDashboard',
    requiresAuth: true,
  },
  {
    label: 'Training',
    path: '/training-courses-catalog',
    icon: 'BookOpen',
    requiresAuth: true,
  },
  {
    label: 'Register',
    path: '/volunteer-registration',
    icon: 'UserPlus',
    requiresAuth: false,
    isPublic: true,
  },
];

const VOLUNTEER_STATUSES = {
  new: { label: 'New Volunteer', className: 'status-new', icon: 'Sprout' },
  trained: { label: 'Trained', className: 'status-trained', icon: 'GraduationCap' },
  established: { label: 'Established', className: 'status-established', icon: 'Star' },
  senior: { label: 'Senior', className: 'status-senior', icon: 'Award' },
};

export default function Header({ isAuthenticated = false, user = null, onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const volunteerStatus = user?.status || 'new';
  const statusConfig = VOLUNTEER_STATUSES?.[volunteerStatus] || VOLUNTEER_STATUSES?.new;

  const visibleNavItems = NAV_ITEMS?.filter(item =>
    item?.isPublic || (isAuthenticated && item?.requiresAuth)
  );

  const isActive = (path) => location?.pathname === path;

  // Close user menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef?.current && !userMenuRef?.current?.contains(e?.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location?.pathname]);

  const handleLogout = () => {
    setUserMenuOpen(false);
    setMobileOpen(false);
    if (onLogout) onLogout();
    navigate('/volunteer-registration');
  };

  const handleKeyDown = (e, action) => {
    if (e?.key === 'Enter' || e?.key === ' ') {
      e?.preventDefault();
      action();
    }
  };

  return (
    <>
      <header className="nav-header" role="banner">
        <div className="w-full max-w-screen-xl mx-auto px-6 flex items-center justify-between h-full">

          {/* Logo */}
          <Link to={isAuthenticated ? '/volunteer-dashboard' : '/volunteer-registration'} className="nav-logo-container" aria-label="Leyland Playscheme Home">
            <img
              src="/assets/images/LPS-1772456086441.png"
              alt="Leyland Playscheme logo"
              className="h-10 w-auto object-contain"
            />
            <span className="nav-logo-text hidden sm:block">
              Leyland<span> Playscheme</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1" aria-label="Primary navigation">
            {visibleNavItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-md font-caption text-sm font-medium
                  transition-all duration-[250ms] ease-smooth
                  focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2
                  ${isActive(item?.path)
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-foreground hover:bg-muted hover:text-primary'
                  }
                `}
                aria-current={isActive(item?.path) ? 'page' : undefined}
              >
                <Icon name={item?.icon} size={16} color="currentColor" />
                {item?.label}
              </Link>
            ))}
          </nav>

          {/* Right Side: Status + User Menu */}
          <div className="flex items-center gap-3">
            {/* Volunteer Status Badge - desktop */}
            {isAuthenticated && user && (
              <div
                className={`status-badge hidden lg:inline-flex ${statusConfig?.className}`}
                title={`Your volunteer status: ${statusConfig?.label}`}
                aria-label={`Volunteer status: ${statusConfig?.label}`}
              >
                <Icon name={statusConfig?.icon} size={12} color="currentColor" />
                {statusConfig?.label}
              </div>
            )}

            {/* User Menu */}
            {isAuthenticated && user ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  onKeyDown={(e) => handleKeyDown(e, () => setUserMenuOpen(!userMenuOpen))}
                  className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-muted transition-all duration-[250ms] ease-smooth focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2"
                  aria-expanded={userMenuOpen}
                  aria-haspopup="true"
                  aria-label="User account menu"
                >
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-foreground font-heading font-semibold text-sm">
                      {user?.name ? user?.name?.charAt(0)?.toUpperCase() : 'V'}
                    </span>
                  </div>
                  <span className="hidden sm:block font-caption text-sm font-medium text-foreground max-w-[120px] truncate">
                    {user?.name || 'Volunteer'}
                  </span>
                  <Icon
                    name={userMenuOpen ? 'ChevronUp' : 'ChevronDown'}
                    size={14}
                    color="currentColor"
                    className="text-muted-foreground hidden sm:block"
                  />
                </button>

                {/* Dropdown */}
                {userMenuOpen && (
                  <div className="user-menu-dropdown animate-fade-in" role="menu" aria-label="User menu options">
                    {/* User Info Header */}
                    <div className="px-4 py-3 border-b border-border">
                      <p className="font-caption text-sm font-semibold text-foreground truncate">
                        {user?.name || 'Volunteer'}
                      </p>
                      <p className="font-caption text-xs text-muted-foreground truncate mt-0.5">
                        {user?.email || ''}
                      </p>
                      {/* Status in dropdown for mobile context */}
                      <div className={`status-badge mt-2 lg:hidden ${statusConfig?.className}`}>
                        <Icon name={statusConfig?.icon} size={11} color="currentColor" />
                        {statusConfig?.label}
                      </div>
                    </div>

                    <Link
                      to="/volunteer-dashboard"
                      className="user-menu-item"
                      role="menuitem"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Icon name="LayoutDashboard" size={16} color="currentColor" />
                      Dashboard
                    </Link>
                    <Link
                      to="/editable-profile-screen"
                      className="user-menu-item"
                      role="menuitem"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Icon name="UserCog" size={16} color="currentColor" />
                      Edit Profile
                    </Link>
                    <Link
                      to="/training-courses-catalog"
                      className="user-menu-item"
                      role="menuitem"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Icon name="BookOpen" size={16} color="currentColor" />
                      My Training
                    </Link>

                    <div className="user-menu-divider" role="separator" />

                    <button
                      className="user-menu-item danger"
                      role="menuitem"
                      onClick={handleLogout}
                    >
                      <Icon name="LogOut" size={16} color="currentColor" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/volunteer-registration"
                className="hidden md:flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md font-caption text-sm font-medium transition-all duration-[250ms] ease-smooth hover:-translate-y-px hover:shadow-md active:scale-[0.97] focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2"
              >
                <Icon name="UserPlus" size={16} color="currentColor" />
                Get Started
              </Link>
            )}

            {/* Mobile Hamburger */}
            <button
              className="md:hidden flex items-center justify-center w-10 h-10 rounded-md hover:bg-muted transition-all duration-[250ms] ease-smooth focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2"
              onClick={() => setMobileOpen(!mobileOpen)}
              onKeyDown={(e) => handleKeyDown(e, () => setMobileOpen(!mobileOpen))}
              aria-expanded={mobileOpen}
              aria-controls="mobile-menu"
              aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
            >
              <Icon name={mobileOpen ? 'X' : 'Menu'} size={22} color="currentColor" />
            </button>
          </div>
        </div>
      </header>
      {/* Mobile Menu */}
      {mobileOpen && (
        <div
          id="mobile-menu"
          className="mobile-menu-overlay animate-fade-in md:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation menu"
        >
          <nav className="px-4 py-4 flex flex-col gap-1" aria-label="Mobile navigation">
            {visibleNavItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-md font-caption text-sm font-medium
                  transition-all duration-[250ms] ease-smooth min-h-[44px]
                  focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2
                  ${isActive(item?.path)
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-foreground hover:bg-muted hover:text-primary'
                  }
                `}
                aria-current={isActive(item?.path) ? 'page' : undefined}
              >
                <Icon name={item?.icon} size={18} color="currentColor" />
                {item?.label}
              </Link>
            ))}

            {!isAuthenticated && (
              <Link
                to="/volunteer-registration"
                className="flex items-center gap-3 px-4 py-3 mt-2 bg-primary text-primary-foreground rounded-md font-caption text-sm font-medium transition-all duration-[250ms] ease-smooth min-h-[44px] active:scale-[0.97]"
              >
                <Icon name="UserPlus" size={18} color="currentColor" />
                Get Started
              </Link>
            )}

            {isAuthenticated && (
              <>
                <div className="h-px bg-border my-2" role="separator" />
                <button
                  className="flex items-center gap-3 px-4 py-3 rounded-md font-caption text-sm font-medium text-error hover:bg-muted transition-all duration-[250ms] ease-smooth min-h-[44px] w-full text-left"
                  onClick={handleLogout}
                >
                  <Icon name="LogOut" size={18} color="currentColor" />
                  Sign Out
                </button>
              </>
            )}
          </nav>
        </div>
      )}
    </>
  );
}